<template>
  <div>
    <ExpansionTable
      :list="list"
      :pagination="pagination"
      :ordering="filters.ordering"
      :loading="isLoading"
      :skeleton="isLoadingAsyncData"
      :readonly="readonly"

      :title="pageTitle"
      :creatable="creatable"

      @change:pagination="onChangePagination"
      @change:sort="onChangeSort"

      @action:create="onActionCreate"
      @action:edit="onActionEdit"
      @action:view="onActionView"
      @action:delete="onActionDelete"
      @action:tpl-download="onActionDownload"
      @action:tpl-upload="onActionUpload"
    />
    <input
      v-show="false"
      ref="input"
      type="file"
      @input="onFileChoose($event.target.files)"
    >
  </div>
</template>

<script>
import { lkPublicationDataCollectionPermissions as Permissions } from '@/helpers/enums/permission-models'
import { createListMixin } from '@/helpers/mixins/createListMixin'
import { PublicationDataCollectionsModel as Model } from './Model'
import { PublicationDataCollectionsStoreModule as StoreModule } from './store'
import { createPublicationDataCollectionModal } from './utils'

import ExpansionTable from './components/PublicationnDataCollectionsExpansionTable.vue'
import { downloadLinkByGet } from '@/helpers/downloadBlob'
import { LkDataCollectionsApi } from '@/api/resources/LkDataCollectionsApi'


const mixinOptions = { Model, StoreModule }
const listMixin = createListMixin(mixinOptions)

/** @typedef { import('@/helpers/mixins/createListMixin').ListMixin<any, StoreModule> } List */

export default {
  name: 'ViewPublicationDataCollectionsList',
  components: {
    ExpansionTable,
  },
  mixins: [
    listMixin,
  ],
  props: {
    publication: {
      type: Object,
      required: true,
      validator: (prop) => (
        prop
        && 'id' in prop
      ),
    },
  },
  data() {
    return {
      dataCollectionId: null,
      isDownloading: false,
      isUploading: false,
      disabledPaginationQuery: true,
      defaultParams: {
        publication_id: this.publication.id,
      },
    }
  },
  computed: {
    readonly() {
      return !this.$access(Permissions.edit)
    },
    creatable() {
      return this.$access(Permissions.create)
    },
  },
  /** @type { List } */
  methods: {
    onActionCreate() {
      const readonly = false
      this.onOpenModal(null, readonly)
    },
    onActionEdit(data) {
      const readonly = false
      this.onOpenModal(data, readonly)
    },
    onActionView(data) {
      const readonly = true
      this.onOpenModal(data, readonly)
    },
    async onActionDownload(data) {
      const api = new LkDataCollectionsApi()
      const link = api.getTemplate(data.id)
      this.isDownloading = true
      try {
        await downloadLinkByGet(link, `${data.name}.xlsx`)
      } catch (e) {
        this.$notice.errors(e)
      } finally {
        this.isDownloading = false
      }
    },
    onActionUpload(data) {
      this.dataCollectionId = data.id
      this.$refs.input.click()
    },

    formatNoticeString(data) {
      return `Для листа "${data.list_name}":<br> - ${data.statistics[0].rows_count_updated} строк обновлено предзаполнением для ${data.statistics[0].users_count} пользователей.<br>`
    },

    async onFileChoose(files) {
      if (this.dataCollectionId === null) return
      const api = new LkDataCollectionsApi()
      const [file] = files
      this.isUploading = true
      try {
        const response = await api.uploadTemplate(this.dataCollectionId, file)
        let noticeString = 'Предзаполнение загружено <br>'
        noticeString += response.map(this.formatNoticeString).join('<br>')

        this.$notice.create({ type: 'success', text: noticeString })
      } catch (e) {
        const errors = e.errors[0]
        if (Array.isArray(errors) && errors.length > 0) {
          let validateErrors = []
          const sliceErrors = errors.slice(0, 8)
          sliceErrors.forEach((err) => {
            validateErrors = validateErrors.concat(`(Лист - ${err.sheet_idx + 1}, Строка - ${err.row_idx + 1}, Столбец - ${err.col_idx + 1}) ${err.error}`)
          })
          this.$notice.warning('Выявлены ошибки при проверке', { type: 'pink' })
          this.$notice.warning(validateErrors.join('<br>'), { type: 'pink', timeout: false })
        }
        this.$notice.errors(e)
      } finally {
        this.$refs.input.value = null
        this.dataCollectionId = null
        this.isUploading = false
      }
    },
    async onActionDelete(data) {
      await this.onDeleteWithRefresh(data, this.defaultParams)
    },
    async onOpenModal(data, readonly) {
      const propsData = await this.getPropsData(data, readonly)
      const modal = await createPublicationDataCollectionModal(this, propsData)
      await modal.open()
      this.onGetData(this.filters)
    },
    getPropsData(data, readonly = false) {
      const action = (payload) => {
        const options = {
          ...this.defaultParams,
          payload: { ...payload, ...this.defaultParams },
          id: payload.id,
        }

        const promise = payload.id
          ? this.update(options)
          : this.create(options)

        return promise
      }

      const propsData = {
        data,
        readonly,
        viewOnly: this.readonly,
        publication: this.publication,
        actionSave: action,
        actionDelete: this.onActionDelete,
        successText: data ? Model.response_success.update : Model.response_success.create,
        errorText: data ? Model.response_error.update : Model.response_error.create,
        ...this.defaultParams,
      }

      return propsData
    },
  },
}
</script>
